import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoginUrlResponse } from '@models/api-customers.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import {
    authExpiresAtKey,
    AuthMode,
    authModeKey,
    authTokensKey,
    LoginState,
    VippsCustomErrorCode,
    VippsErrorCode
} from '@core/authentication/authentication.models';
import { Router } from '@angular/router';
import { StorageService } from '@core/services/storage.service';
import { NotificationService } from '@core/services/notification/notification.service';

import { TranslationKey } from '@shared/translations/auto-generated/translation-key';
import { TranslateService } from '@ngx-translate/core';
import { isTokenExpired } from '@core/authentication/authentication.helper';
import { distinctUntilChanged } from 'rxjs/operators';
import isEqual from 'lodash.isequal';
import { unauthorizedContentRedirectKey } from '@core/authentication/redirect-util';

const ONE_HOUR_IN_MS = 60 * 60 * 1000; // 3600000 milliseconds
const DEFAULT_TTL_HOURS = 1; // Default time to live in hours

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class VippsAuthenticationService {
    private timeToShowWarningInMinutes = 5; // 5 minutes
    private warningTimer: NodeJS.Timeout | null = null;
    private loginStateSnapshot: LoginState = { loggedIn: false };
    private loginStateRs$ = new BehaviorSubject<LoginState>(this.loginStateSnapshot);
    public loginState$ = this.loginStateRs$.asObservable();

    constructor(
        private router: Router,
        private ngZone: NgZone,
        private httpClient: HttpClient,
        private translateService: TranslateService,
        private notificationService: NotificationService,
        private storageService: StorageService
    ) {
        this.loginState$
            .pipe(
                untilDestroyed(this),
                distinctUntilChanged((a, b) => isEqual(a, b))
            )
            .subscribe(loginState => {
                const { loggedIn, token } = loginState;
                this.loginStateSnapshot = loginState;
                if (loggedIn && token) {
                    this.startWarningTimer();
                }
            });
    }

    public init(mode?: AuthMode): void {
        const authMode: AuthMode | undefined = mode ?? this.storageService.get(authModeKey);
        if (authMode !== 'Vipps') {
            return;
        }

        this.storageService.set(authModeKey, authMode);
        const savedToken = this.getSavedToken();
        const expiresAt = this.storageService.get(authExpiresAtKey);
        if (savedToken) {
            this.loginStateRs$.next({ loggedIn: true, token: savedToken, expiresAt, authMode: 'Vipps' });
            return;
        }

        this.loginStateRs$.next({ loggedIn: false, authMode: 'Vipps' });
    }

    public login(): void {
        this.httpClient
            .get<LoginUrlResponse>('customers-ms/authentication/VippsLogin')
            .pipe(untilDestroyed(this))
            .subscribe(response => {
                if (!response.loginUrl) {
                    return;
                }

                location.href = response.loginUrl;
            });
    }

    public logout(): Promise<void> {
        this.deleteSavedToken();
        return this.router.navigateByUrl('/').then(() => {
            this.loginStateRs$.next({ loggedIn: false });
        });
    }

    public updateToken(token?: string, expiresAt?: string, error?: VippsErrorCode & VippsCustomErrorCode): void {
        if (error) {
            this.loginStateRs$.error({ loggedIn: false, error: error, authMode: 'Vipps' });
            return;
        }

        if (token && expiresAt) {
            if (isTokenExpired(expiresAt)) {
                this.deleteSavedToken();
                this.loginStateRs$.error({ loggedIn: false, error: 'expired_token', authMode: 'Vipps' });
                return;
            }

            this.saveToken(token, expiresAt);
            this.loginStateRs$.next({ loggedIn: true, token, expiresAt, authMode: 'Vipps' });
            return;
        }

        this.deleteSavedToken();
    }

    public clearTokens(): void {
        this.deleteSavedToken();
    }

    private saveToken(token: string, expiresAt?: string): void {
        if (this.storageService.isStorageSupported) {
            const expiresAtDate = expiresAt ? new Date(expiresAt) : new Date();
            const isValidDate = !isNaN(expiresAtDate.getTime());
            const currentTime = new Date().getTime();
            const timeToLive = isValidDate
                ? Math.round((expiresAtDate.getTime() - currentTime) / ONE_HOUR_IN_MS)
                : DEFAULT_TTL_HOURS;

            this.storageService.set(authModeKey, 'Vipps', { hoursToLive: timeToLive });
            this.storageService.set(authExpiresAtKey, expiresAtDate, { hoursToLive: timeToLive });
            this.storageService.set(authTokensKey, token, { hoursToLive: timeToLive });
        }
    }

    private getSavedToken(): string | undefined {
        if (this.storageService.isStorageSupported) {
            return this.storageService.get(authTokensKey);
        }
    }

    private deleteSavedToken(): void {
        // let be sure we will remove the information about the membership after logout
        this.storageService.remove('membership-data');
        this.storageService.remove(unauthorizedContentRedirectKey);
        this.storageService.remove(authModeKey);
        this.storageService.remove(authTokensKey);
        this.storageService.remove(authExpiresAtKey);
    }

    /**
     * Start the warning timer, this will trigger a warning notification to the user 5 minutes before the token expires
     */
    private startWarningTimer(): void {
        const expiresAt = this.storageService.get(authExpiresAtKey);
        if (!expiresAt) {
            return;
        }

        if (this.warningTimer) {
            clearTimeout(this.warningTimer);
        }

        const expiresAtDate = new Date(expiresAt);
        const warningTime = new Date(expiresAtDate.getTime() - this.timeToShowWarningInMinutes * 60 * 1000);
        const timeLeftInMinutes = Math.round((expiresAtDate.getTime() - new Date().getTime()) / 60 / 1000);

        this.ngZone.runOutsideAngular(() => {
            this.warningTimer = setTimeout(() => {
                this.ngZone.run(() => {
                    let headline: TranslationKey = this.translateService.instant(
                        'vipps.message.tokenExpiresHeadline'
                    ) as TranslationKey;
                    let message: TranslationKey = this.translateService.instant('vipps.message.tokenExpires', {
                        minutes: timeLeftInMinutes
                    }) as TranslationKey;

                    if (expiresAtDate.getTime() < new Date().getTime()) {
                        message = this.translateService.instant('vipps.message.tokenExpired') as TranslationKey;
                        headline = this.translateService.instant(
                            'vipps.message.tokenExpiredHeadline'
                        ) as TranslationKey;
                    }

                    this.notificationService.createNotification({
                        fadeTime: 0,
                        type: 'info',
                        translationHeadline: headline,
                        messageText: message
                    });
                });
            }, warningTime.getTime() - new Date().getTime());
        });
    }
}
